import React, { useState, useEffect, useMemo } from 'react';
import { useNumberInput } from '../../hooks';
import {
  Box,
  Typography,
  Paper,
  Button,
  Alert,
  TextField,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  IconButton,
  CircularProgress,
  TablePagination,
  InputAdornment,
  Snackbar,
  Link,
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as ApproveIcon,
  Cancel as CancelIcon,
  Person as PersonIcon,
  Add as AddIcon,
  PersonOff as OfflinePlayerIcon,
  GridOn as GridIcon,
  Search as SearchIcon,
  Receipt as ReceiptIcon,
  MergeType as MergeIcon,
  Schedule as ScheduleIcon,
  People as ConnectionsIcon,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { usePagination } from '../../hooks/core';
import { IconPicker, ConfirmDialog, MergeOfflinePlayerDialog } from '../../components';
import { BoardPaymentStatus } from './BoardPaymentStatus';
import type { Board, User, Hit, LedgerEntry, LedgerSummary, UserBalance, OfflinePlayer, BoardOfflinePlayer, BoardParticipant, MergeRequest, GlobalMergeRequestResult } from '../../types';
import { getTotalCells } from '../../types';
import { squareRequestsApi, offlinePlayersApi } from '../../api';

export interface LedgerPanelProps {
  summary: LedgerSummary | null;
  ledgerEntries: LedgerEntry[];
  board: Board;
  hits: Hit[];
  isManager: boolean;
  isOwner: boolean;
  currentUserId?: string;
  onRecordPayment: (userId: string | undefined, offlinePlayerId: string | undefined, amount: number, description: string) => Promise<void>;
  onMarkWinningsPaid: (userId: string | undefined, offlinePlayerId: string | undefined, paid: boolean) => Promise<void>;
  // Player management props
  players: { userId: string; user?: User; role: 'owner' | 'manager' | 'player'; joinedAt?: string; approvedSquares?: number; claimedSquares?: number }[];
  offlinePlayers: OfflinePlayer[];
  participants: BoardParticipant[];
  onPromoteToManager: (userId: string) => Promise<void>;
  onRemoveManager: (userId: string) => Promise<void>;
  onRemoveParticipant?: (userId: string) => Promise<void>;
  onOfflinePlayersChange: () => void;
  onPlayerRemoved?: (playerId: string) => void;
  pendingMergeRequests?: MergeRequest[];
  onCancelMergeRequest?: (requestId: string) => Promise<void>;
  onMergeRequestCreated?: () => void;
  onOpenAddConnectionDialog?: () => void;
  /** Initial offline player ID for merge suggestion (from notification) */
  mergeSuggestionOfflinePlayerId?: string;
  /** Initial target user ID for merge suggestion (from notification) */
  mergeSuggestionTargetUserId?: string;
  /** Callback when merge suggestion is consumed */
  onMergeSuggestionConsumed?: () => void;
}

type BalanceFilter = 'all' | 'paid' | 'unpaid' | 'winners' | 'online' | 'offline';

export const LedgerPanel: React.FC<LedgerPanelProps> = ({
  summary, ledgerEntries, board, hits, isManager, isOwner, currentUserId, onRecordPayment, onMarkWinningsPaid,
  players, offlinePlayers, participants, onPromoteToManager, onRemoveManager, onRemoveParticipant, onOfflinePlayersChange, onPlayerRemoved,
  pendingMergeRequests = [], onCancelMergeRequest, onMergeRequestCreated, onOpenAddConnectionDialog,
  mergeSuggestionOfflinePlayerId, mergeSuggestionTargetUserId, onMergeSuggestionConsumed
}) => {
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<{ userId?: string; offlinePlayerId?: string; name: string; amount: number } | null>(null);
  const [paymentAmount, setPaymentAmount] = useState(0);
  const [paymentNotes, setPaymentNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState<BalanceFilter>('all');
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [summaryModalOpen, setSummaryModalOpen] = useState(false);
  const [copiedSummary, setCopiedSummary] = useState(false);
  const [cancellingRequestId, setCancellingRequestId] = useState<string | null>(null);
  const { paginationProps, resetPage, getPaginatedItems } = usePagination();

  // Offline player management state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPlayer, setEditingPlayer] = useState<OfflinePlayer | null>(null);
  const [displayName, setDisplayName] = useState('');
  const [handle, setHandle] = useState('');
  const [iconName, setIconName] = useState('');
  const [editApprovedSquares, setEditApprovedSquares] = useState<number>(0);
  const [newPlayerStartingBalance, setNewPlayerStartingBalance] = useState<number>(0);
  const [playerError, setPlayerError] = useState<string | null>(null);

  // State for promote/remove manager confirmation dialogs
  const [promoteConfirmOpen, setPromoteConfirmOpen] = useState(false);
  const [removeConfirmOpen, setRemoveConfirmOpen] = useState(false);
  const [pendingManagerAction, setPendingManagerAction] = useState<{ userId: string; displayName: string } | null>(null);

  const handleOpenPromoteConfirm = (userId: string, displayName: string) => {
    setPendingManagerAction({ userId, displayName });
    setPromoteConfirmOpen(true);
  };

  const handleOpenRemoveConfirm = (userId: string, displayName: string) => {
    setPendingManagerAction({ userId, displayName });
    setRemoveConfirmOpen(true);
  };

  const handleConfirmPromote = async () => {
    if (!pendingManagerAction) return;
    try {
      await onPromoteToManager(pendingManagerAction.userId);
    } finally {
      setPromoteConfirmOpen(false);
      setPendingManagerAction(null);
    }
  };

  const handleConfirmRemove = async () => {
    if (!pendingManagerAction) return;
    try {
      await onRemoveManager(pendingManagerAction.userId);
    } finally {
      setRemoveConfirmOpen(false);
      setPendingManagerAction(null);
    }
  };

  // State for remove participant confirmation
  const [removeParticipantConfirmOpen, setRemoveParticipantConfirmOpen] = useState(false);
  const [pendingParticipantRemoval, setPendingParticipantRemoval] = useState<{ userId: string; displayName: string } | null>(null);

  const handleOpenRemoveParticipantConfirm = (userId: string, displayName: string) => {
    setPendingParticipantRemoval({ userId, displayName });
    setRemoveParticipantConfirmOpen(true);
  };

  const handleConfirmRemoveParticipant = async () => {
    if (!pendingParticipantRemoval || !onRemoveParticipant) return;
    try {
      await onRemoveParticipant(pendingParticipantRemoval.userId);
    } finally {
      setRemoveParticipantConfirmOpen(false);
      setPendingParticipantRemoval(null);
    }
  };

  // Number input hooks for better typing UX
  const paymentInput = useNumberInput(paymentAmount, setPaymentAmount, { min: 0 });
  const approvedSquaresInput = useNumberInput(editApprovedSquares, setEditApprovedSquares, { min: 0, allowDecimals: false });
  const startingBalanceInput = useNumberInput(newPlayerStartingBalance, setNewPlayerStartingBalance, { min: 0 });

  // State for assigning squares to online players
  const [assignSquaresDialogOpen, setAssignSquaresDialogOpen] = useState(false);
  const [assignSquaresTarget, setAssignSquaresTarget] = useState<{ userId: string; displayName: string } | null>(null);
  const [assignSquaresCount, setAssignSquaresCount] = useState<number>(1);
  const [assigningSquares, setAssigningSquares] = useState(false);
  const [assignSquaresError, setAssignSquaresError] = useState<string | null>(null);

  // State for merge dialog
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
  const [mergeSuccess, setMergeSuccess] = useState<string | null>(null);

  // Open merge dialog when merge suggestion props are provided (from notification)
  useEffect(() => {
    if (mergeSuggestionOfflinePlayerId && mergeSuggestionTargetUserId) {
      setMergeDialogOpen(true);
      onMergeSuggestionConsumed?.();
    }
  }, [mergeSuggestionOfflinePlayerId, mergeSuggestionTargetUserId, onMergeSuggestionConsumed]);

  // Toggle row expansion
  const toggleRowExpansion = (key: string) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  // Get hits for a specific user/offlinePlayer - returns their winning hits
  const getWinningHitsForUser = (userId?: string, offlinePlayerId?: string): Hit[] => {
    return hits.filter(hit => {
      if (!hit.winningSquare) return false;
      if (userId && hit.winningSquare.ownerId === userId) return true;
      if (offlinePlayerId && hit.winningSquare.offlinePlayerId === offlinePlayerId) return true;
      return false;
    });
  };

  // Get bad numbers ledger entries for a specific user/offlinePlayer
  const getBadNumbersEntriesForUser = (userId?: string, offlinePlayerId?: string): LedgerEntry[] => {
    return (ledgerEntries || []).filter((entry: LedgerEntry) => {
      if (entry.entryType !== 'bad_numbers') return false;
      if (userId && entry.userId === userId) return true;
      if (offlinePlayerId && entry.offlinePlayerId === offlinePlayerId) return true;
      return false;
    });
  };

  // Get neighbor payout ledger entries for a specific user/offlinePlayer
  const getNeighborPayoutEntriesForUser = (userId?: string, offlinePlayerId?: string): LedgerEntry[] => {
    return (ledgerEntries || []).filter((entry: LedgerEntry) => {
      if (entry.entryType !== 'payout') return false;
      const isNeighborPayout = entry.description.includes('Neighbor payout') || entry.description.includes('Box neighbor payout');
      if (!isNeighborPayout) return false;
      if (userId && entry.userId === userId) return true;
      if (offlinePlayerId && entry.offlinePlayerId === offlinePlayerId) return true;
      return false;
    });
  };

  // Get hit type label
  const getHitTypeLabel = (hit: Hit): string => {
    if (hit.isPlusN && hit.isWrongWay) return `+${board.payoutConfig?.plusNOffset ?? 5} WW`;
    if (hit.isPlusN) return `+${board.payoutConfig?.plusNOffset ?? 5}`;
    if (hit.isWrongWay) return 'Wrong Way';
    if (hit.categoryId) return 'Custom';
    return 'Main';
  };

  // Get quarter/period label based on board sport
  const getQuarterLabel = (quarter: number | null | undefined): string => {
    if (quarter === null || quarter === undefined) return '';
    if (quarter === 0) return 'Final (OT)';
    const quarters = board.quarters || 4;
    if (quarters === 2) {
      return quarter === 1 ? '1st Half' : '2nd Half';
    }
    if (quarters === 3) {
      return `${quarter}${quarter === 1 ? 'st' : quarter === 2 ? 'nd' : 'rd'} Period`;
    }
    return `Q${quarter}`;
  };

  // Assign squares to online player handlers
  const handleOpenAssignSquares = (userId: string, displayName: string) => {
    setAssignSquaresTarget({ userId, displayName });
    setAssignSquaresCount(1);
    setAssignSquaresError(null);
    setAssignSquaresDialogOpen(true);
  };

  const handleCloseAssignSquares = () => {
    setAssignSquaresDialogOpen(false);
    setAssignSquaresTarget(null);
    setAssignSquaresCount(1);
    setAssignSquaresError(null);
  };

  const handleConfirmAssignSquares = async () => {
    if (!assignSquaresTarget || assignSquaresCount <= 0) return;
    setAssigningSquares(true);
    setAssignSquaresError(null);
    try {
      await squareRequestsApi.assignSquares(board.id, {
        userId: assignSquaresTarget.userId,
        squares: assignSquaresCount,
      });
      handleCloseAssignSquares();
      onOfflinePlayersChange();
    } catch (err) {
      setAssignSquaresError(err instanceof Error ? err.message : 'Failed to assign squares');
    } finally {
      setAssigningSquares(false);
    }
  };

  const handleOpenDialog = (player?: OfflinePlayer) => {
    if (player) {
      setEditingPlayer(player);
      setDisplayName(player.displayName);
      setHandle(player.handle || '');
      setIconName(player.iconName || '');
      const boardPlayer = player as BoardOfflinePlayer;
      setEditApprovedSquares(boardPlayer.approvedSquares ?? 0);
      setNewPlayerStartingBalance(boardPlayer.startingBalance ?? 0);
    } else {
      setEditingPlayer(null);
      setDisplayName('');
      setHandle('');
      setIconName('');
      setEditApprovedSquares(1);
      setNewPlayerStartingBalance(0);
    }
    setPlayerError(null);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingPlayer(null);
    setDisplayName('');
    setHandle('');
    setIconName('');
    setEditApprovedSquares(0);
    setNewPlayerStartingBalance(0);
    setPlayerError(null);
  };

  const handleSavePlayer = async () => {
    if (!displayName.trim()) {
      setPlayerError('Display name is required');
      return;
    }
    setSaving(true);
    setPlayerError(null);
    try {
      if (editingPlayer) {
        await offlinePlayersApi.updateBoardOfflinePlayer(board.id, editingPlayer.id, {
          displayName: displayName.trim(),
          handle: handle.trim() || undefined,
          iconName: iconName.trim() || undefined,
          approvedSquares: editApprovedSquares,
        });
      } else {
        await offlinePlayersApi.createBoardOfflinePlayer(board.id, {
          displayName: displayName.trim(),
          handle: handle.trim() || undefined,
          iconName: iconName.trim() || undefined,
          approvedSquares: editApprovedSquares,
          startingBalance: newPlayerStartingBalance !== 0 ? newPlayerStartingBalance : undefined,
        });
      }
      handleCloseDialog();
      onOfflinePlayersChange();
    } catch (err) {
      setPlayerError(err instanceof Error ? err.message : 'Failed to save offline player');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveFromBoard = async (playerId: string) => {
    if (!confirm('Remove this player from the board? Any squares assigned to them will be cleared.')) return;
    try {
      await offlinePlayersApi.removeFromBoard(board.id, playerId);
      onPlayerRemoved?.(playerId);
      onOfflinePlayersChange();
    } catch (err) {
      setPlayerError(err instanceof Error ? err.message : 'Failed to remove player from board');
    }
  };

  const handleRequestCreated = (request: MergeRequest) => {
    const targetName = request.targetUser?.displayName || 'the user';
    setMergeSuccess(`Merge request sent to ${targetName}. They must approve it before squares are transferred.`);
    setMergeDialogOpen(false);
    onMergeRequestCreated?.();
    setTimeout(() => setMergeSuccess(null), 5000);
  };

  const handleGlobalRequestCreated = (result: GlobalMergeRequestResult) => {
    const boardCount = result.boardSummaries.length;
    const targetName = result.requests[0]?.targetUser?.displayName || 'the user';
    const boardNames = result.boardSummaries.map(b => b.boardName).join(', ');
    const currentBoardIncluded = result.boardSummaries.some(b => b.boardId === board.id);

    let message = `Global merge request sent to ${targetName} for ${result.totalSquares} square${result.totalSquares !== 1 ? 's' : ''} across ${boardCount} board${boardCount !== 1 ? 's' : ''}: ${boardNames}.`;

    if (!currentBoardIncluded) {
      message += ` Note: This board is not included. Check the other boards to track progress.`;
    }

    message += ` They must approve it before squares are transferred.`;

    setMergeSuccess(message);
    setMergeDialogOpen(false);
    onMergeRequestCreated?.();
    setTimeout(() => setMergeSuccess(null), 10000);
  };

  // Helper to get display name for a balance
  const getDisplayName = (balance: UserBalance) => {
    if (balance.isOfflinePlayer && balance.offlinePlayer) {
      return balance.offlinePlayer.displayName;
    }
    return balance.user?.displayName || 'Unknown';
  };

  // Generate accounting summary text for sharing
  const generateAccountingSummary = (balances: UserBalance[]) => {
    const sortedBalances = [...balances].sort((a, b) => b.netBalance - a.netBalance);
    const winners = sortedBalances.filter(b => b.netBalance > 0);
    const owes = sortedBalances.filter(b => b.netBalance < 0);
    const breakEven = sortedBalances.filter(b => b.netBalance === 0 && b.claimedSquares > 0);

    const lines: string[] = [];
    winners.forEach(b => { lines.push(`${getDisplayName(b)} +${Math.round(b.netBalance)}`); });
    breakEven.forEach(b => { lines.push(`${getDisplayName(b)} 0`); });
    owes.forEach(b => { lines.push(`${getDisplayName(b)} ${Math.round(b.netBalance)}`); });

    return lines.join('\n');
  };

  const handleCopySummary = async () => {
    const summaryText = generateAccountingSummary(baseBalances);
    try {
      await navigator.clipboard.writeText(summaryText);
      setCopiedSummary(true);
      setTimeout(() => setCopiedSummary(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Non-managers only see their own balance
  const allBalances = summary?.balances || [];
  const baseBalances = isManager ? allBalances : allBalances.filter(b => !b.isOfflinePlayer && b.userId === currentUserId);
  const pricePerSquare = board.pricePerSquare;
  const isCompleted = board.status === 'completed';
  const isBoardLocked = board.status === 'locked' || board.status === 'completed';
  const canMarkWinningsPaid = isManager && (isCompleted || board.status === 'live' || board.status === 'locked');

  // Apply filter and search
  const filteredBalances = useMemo(() => {
    let result = baseBalances;

    switch (filter) {
      case 'paid':
        result = result.filter(b => (b.totalOwed - b.totalPaid) <= 0);
        break;
      case 'unpaid':
        result = result.filter(b => (b.totalOwed - b.totalPaid) > 0);
        break;
      case 'winners':
        result = result.filter(b => b.totalEarned > 0);
        break;
      case 'online':
        result = result.filter(b => !b.isOfflinePlayer);
        break;
      case 'offline':
        result = result.filter(b => b.isOfflinePlayer);
        break;
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      result = result.filter(b => {
        const name = getDisplayName(b).toLowerCase();
        const userHandle = b.user?.handle?.toLowerCase() || '';
        const offlineHandle = b.offlinePlayer?.handle?.toLowerCase() || '';
        return name.includes(query) || userHandle.includes(query) || offlineHandle.includes(query);
      });
    }

    return result;
  }, [baseBalances, filter, searchQuery]);

  // Calculate counts for filter badges
  const paidCount = baseBalances.filter(b => (b.totalOwed - b.totalPaid) <= 0).length;
  const unpaidCount = baseBalances.filter(b => (b.totalOwed - b.totalPaid) > 0).length;
  const winnersCount = baseBalances.filter(b => b.totalEarned > 0).length;
  const onlineCount = baseBalances.filter(b => !b.isOfflinePlayer).length;
  const offlineCount = baseBalances.filter(b => b.isOfflinePlayer).length;

  const handleOpenPaymentDialog = (balance: UserBalance) => {
    const amountDue = balance.totalOwed - balance.totalPaid;
    setSelectedUser({
      userId: balance.isOfflinePlayer ? undefined : balance.userId,
      offlinePlayerId: balance.isOfflinePlayer ? balance.offlinePlayerId : undefined,
      name: getDisplayName(balance),
      amount: amountDue > 0 ? amountDue : 0,
    });
    setPaymentAmount(amountDue > 0 ? amountDue : 0);
    setPaymentNotes('');
    setPaymentDialogOpen(true);
  };

  const handleRecordPayment = async () => {
    if (!selectedUser || paymentAmount <= 0) return;
    setSaving(true);
    try {
      const description = paymentNotes.trim() || 'Payment for squares';
      await onRecordPayment(selectedUser.userId, selectedUser.offlinePlayerId, paymentAmount, description);
      setPaymentDialogOpen(false);
      setSelectedUser(null);
      setPaymentNotes('');
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleWinningsPaidChange = async (balance: UserBalance, currentlyPaid: boolean) => {
    setSaving(true);
    try {
      const userId = balance.isOfflinePlayer ? undefined : balance.userId;
      const offlinePlayerId = balance.isOfflinePlayer ? balance.offlinePlayerId : undefined;
      await onMarkWinningsPaid(userId, offlinePlayerId, !currentlyPaid);
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleFilterChange = (newFilter: BalanceFilter) => {
    setFilter(newFilter);
    resetPage();
  };

  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
    resetPage();
  };

  // Sort balances: unpaid first (by amount due), then paid
  const sortedBalances = [...filteredBalances].sort((a, b) => {
    const aOwes = a.totalOwed - a.totalPaid;
    const bOwes = b.totalOwed - b.totalPaid;
    return bOwes - aOwes;
  });

  // Paginate
  const paginatedBalances = getPaginatedItems(sortedBalances);

  // Calculate outstanding amounts for the message
  const totalUnpaidSquares = baseBalances.reduce((sum, b) => sum + Math.max(0, b.totalOwed - b.totalPaid), 0);
  const totalUnpaidWinnings = baseBalances.filter(b => b.totalEarned > 0 && !b.winningsFullyPaid).reduce((sum, b) => sum + b.totalEarned, 0);

  // Calculate board-level payment stats for BoardPaymentStatus
  const totalCells = board.totalCells || getTotalCells(board.cellMode || 'standard');
  const squaresPerCell = 100 / totalCells;
  const isStandardMode = totalCells === 100;

  const totalClaimedSquares = baseBalances.reduce((sum, b) => sum + b.claimedSquares, 0);
  const totalClaimedCells = Math.round(totalClaimedSquares / squaresPerCell);
  const totalPaidAmount = baseBalances.reduce((sum, b) => sum + b.totalPaid, 0);

  return (
    <Box>
      {/* Explanation box */}
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          {isManager
            ? `Track what each player owes for their squares and what winnings they've earned. Record payments and mark winnings as paid out.`
            : `View your balance for this board including square costs, payments made, and any winnings earned.`
          }
          {' '}
          <Link component={RouterLink} to="/support/accounting#players-tab">
            Learn more →
          </Link>
        </Typography>
      </Alert>


      {/* Board Payment Status - show for managers */}
      {isManager && (
        <BoardPaymentStatus
          pricePerSquare={pricePerSquare}
          totalCells={totalCells}
          claimedCells={totalClaimedCells}
          totalPaid={totalPaidAmount}
          boardStatus={board.status}
          profitPercent={board.payoutConfig?.profitPercent ?? 0}
          isStandardMode={isStandardMode}
        />
      )}

      {/* Completed board guidance for managers */}
      {isManager && isCompleted && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
            Board Complete - Time to Settle Up!
          </Typography>
          <Typography variant="body2">
            {totalUnpaidSquares > 0 || totalUnpaidWinnings > 0 ? (
              <>
                {totalUnpaidSquares > 0 && `Collect $${totalUnpaidSquares.toFixed(2)} in outstanding square payments. `}
                {totalUnpaidWinnings > 0 && `Pay out $${totalUnpaidWinnings.toFixed(2)} in winnings to winners.`}
              </>
            ) : (
              'All payments collected and winnings distributed. Great job!'
            )}
          </Typography>
        </Alert>
      )}

      {/* Pending Merge Requests - for managers */}
      {isManager && pendingMergeRequests.length > 0 && (
        <Paper
          elevation={2}
          sx={{
            mb: 3,
            p: 2,
            bgcolor: 'warning.50',
            border: '1px solid',
            borderColor: 'warning.200',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <MergeIcon color="warning" />
            <Typography variant="subtitle1" fontWeight="bold">
              Pending Merge Requests
            </Typography>
            <Chip
              label={pendingMergeRequests.length}
              size="small"
              color="warning"
            />
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            These merge requests are waiting for players to approve. The player must approve before their squares can be transferred.
          </Typography>
          {pendingMergeRequests.map((request) => (
            <Paper
              key={request.id}
              variant="outlined"
              sx={{ p: 2, mb: 1, bgcolor: 'background.paper' }}
            >
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { sm: 'center' }, justifyContent: 'space-between' }}>
                <Box sx={{ flex: 1 }}>
                  {request.globalRequestId && (
                    <Box sx={{ mb: 0.5 }}>
                      <Chip label="Global Merge" size="small" color="secondary" />
                    </Box>
                  )}
                  <Typography variant="body1">
                    Merge <strong>{request.offlinePlayer?.displayName || 'Unknown Player'}</strong>&apos;s {request.squaresToTransfer} square{request.squaresToTransfer !== 1 ? 's' : ''} → <strong>{request.targetUser?.displayName || 'Unknown User'}</strong>
                    {request.globalRequestId && (
                      <Typography component="span" variant="body2" color="text.secondary">
                        {' '}(this board)
                      </Typography>
                    )}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                    <ScheduleIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                    <Typography variant="caption" color="text.secondary">
                      Waiting for {request.targetUser?.displayName || 'player'} to approve &bull; Expires {new Date(request.expiresAt).toLocaleDateString()}
                    </Typography>
                  </Box>
                  {request.removeOfflinePlayer && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      The offline player will be removed after merge
                    </Typography>
                  )}
                </Box>
                {onCancelMergeRequest && request.requestedById === currentUserId && (
                  <Tooltip title="Cancel this merge request">
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      startIcon={cancellingRequestId === request.id ? <CircularProgress size={16} /> : <CancelIcon />}
                      onClick={async () => {
                        setCancellingRequestId(request.id);
                        try {
                          await onCancelMergeRequest(request.id);
                        } finally {
                          setCancellingRequestId(null);
                        }
                      }}
                      disabled={cancellingRequestId !== null}
                    >
                      {cancellingRequestId === request.id ? 'Cancelling...' : 'Cancel'}
                    </Button>
                  </Tooltip>
                )}
              </Box>
            </Paper>
          ))}
        </Paper>
      )}

      {/* Summary stats */}
      {summary && (
        <Paper sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
          <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            <Box>
              <Typography variant="overline" color="text.secondary">Total Pot</Typography>
              <Typography variant="h6">${summary.totalPot.toFixed(2)}</Typography>
            </Box>
            <Box>
              <Typography variant="overline" color="text.secondary">Winnings Paid Out</Typography>
              <Typography variant="h6">${summary.totalPaidOut.toFixed(2)}</Typography>
            </Box>
            <Box>
              <Typography variant="overline" color="text.secondary">Price/Square</Typography>
              <Typography variant="h6">${pricePerSquare.toFixed(2)}</Typography>
            </Box>
          </Box>
        </Paper>
      )}

      {/* Search and Filters */}
      {isManager && (
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <TextField
              size="small"
              placeholder="Search players..."
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              sx={{ width: { xs: '100%', sm: 300 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <Box sx={{ flex: 1 }} />
            {isOwner && (
              <Tooltip title="View accounting summary for sharing">
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<ReceiptIcon />}
                  onClick={() => setSummaryModalOpen(true)}
                >
                  Summary
                </Button>
              </Tooltip>
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            <Chip label={`All (${baseBalances.length})`} onClick={() => handleFilterChange('all')} color={filter === 'all' ? 'primary' : 'default'} variant={filter === 'all' ? 'filled' : 'outlined'} size="small" />
            <Chip label={`Paid (${paidCount})`} onClick={() => handleFilterChange('paid')} color={filter === 'paid' ? 'success' : 'default'} variant={filter === 'paid' ? 'filled' : 'outlined'} size="small" />
            <Chip label={`Unpaid (${unpaidCount})`} onClick={() => handleFilterChange('unpaid')} color={filter === 'unpaid' ? 'warning' : 'default'} variant={filter === 'unpaid' ? 'filled' : 'outlined'} size="small" />
            <Chip label={`Winners (${winnersCount})`} onClick={() => handleFilterChange('winners')} color={filter === 'winners' ? 'info' : 'default'} variant={filter === 'winners' ? 'filled' : 'outlined'} size="small" />
            <Box sx={{ width: 8 }} />
            <Chip label={`Online (${onlineCount})`} onClick={() => handleFilterChange('online')} color={filter === 'online' ? 'primary' : 'default'} variant={filter === 'online' ? 'filled' : 'outlined'} size="small" icon={<PersonIcon sx={{ fontSize: 16 }} />} />
            <Chip label={`Offline (${offlineCount})`} onClick={() => handleFilterChange('offline')} color={filter === 'offline' ? 'secondary' : 'default'} variant={filter === 'offline' ? 'filled' : 'outlined'} size="small" icon={<OfflinePlayerIcon sx={{ fontSize: 16 }} />} />
            <Box sx={{ display: { xs: 'none', sm: 'flex' }, flex: 1 }} />
            <Box sx={{ display: { xs: 'none', sm: 'flex' }, gap: 1 }}>
              {onOpenAddConnectionDialog && (
                <Button size="small" variant="contained" startIcon={<ConnectionsIcon />} onClick={onOpenAddConnectionDialog}>
                  Add Player
                </Button>
              )}
              <Button size="small" variant="text" startIcon={<AddIcon />} onClick={() => handleOpenDialog()} data-testid="create-offline-player-button">
                Create Offline
              </Button>
              {offlinePlayers.length > 0 && participants.length > 0 && (
                <Button size="small" variant="outlined" color="primary" startIcon={<MergeIcon />} onClick={() => setMergeDialogOpen(true)}>
                  Request Merge
                </Button>
              )}
            </Box>
          </Box>

          {/* Add Player buttons - separate row on mobile */}
          <Box sx={{ display: { xs: 'flex', sm: 'none' }, gap: 1, mb: 1, justifyContent: 'flex-start', flexWrap: 'wrap' }}>
            {onOpenAddConnectionDialog && (
              <Button size="small" variant="contained" startIcon={<ConnectionsIcon />} onClick={onOpenAddConnectionDialog}>
                Add Player
              </Button>
            )}
            <Button size="small" variant="text" startIcon={<AddIcon />} onClick={() => handleOpenDialog()} data-testid="create-offline-player-button">
              Create Offline
            </Button>
            {offlinePlayers.length > 0 && participants.length > 0 && (
              <Button size="small" variant="outlined" color="primary" startIcon={<MergeIcon />} onClick={() => setMergeDialogOpen(true)}>
                Request Merge
              </Button>
            )}
          </Box>

          {/* Player Type Legend */}
          <Paper sx={{ p: 1.5, bgcolor: 'grey.50', mt: 2 }}>
            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PersonIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                <Box>
                  <Typography variant="caption" fontWeight="bold">Online Players ({onlineCount})</Typography>
                  <Typography variant="caption" color="text.secondary" display="block">Users with accounts who joined via invite</Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <OfflinePlayerIcon sx={{ color: 'secondary.main', fontSize: 20 }} />
                <Box>
                  <Typography variant="caption" fontWeight="bold">Offline Players ({offlineCount})</Typography>
                  <Typography variant="caption" color="text.secondary" display="block">Cash players managed by you</Typography>
                </Box>
              </Box>
            </Box>
          </Paper>

          {/* Pending merge requests indicator */}
          {pendingMergeRequests.length > 0 && (
            <Alert severity="info" icon={<MergeIcon />} sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>{pendingMergeRequests.length} pending merge request{pendingMergeRequests.length !== 1 ? 's' : ''}</strong> awaiting approval:
              </Typography>
              <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2 }}>
                {pendingMergeRequests.map((req) => (
                  <li key={req.id}>
                    <Typography variant="body2" color="text.secondary">
                      {req.offlinePlayer?.displayName || 'Unknown'} → {req.targetUser?.displayName || 'Unknown'}
                      {req.globalRequestId && (
                        <Chip label="Global" size="small" color="secondary" sx={{ ml: 1 }} />
                      )}
                      {' '}
                      <Typography component="span" variant="caption" color="text.disabled">
                        (expires {new Date(req.expiresAt).toLocaleDateString()})
                      </Typography>
                    </Typography>
                  </li>
                ))}
              </Box>
            </Alert>
          )}
        </Box>
      )}

      {!summary ? (
        <Typography color="text.secondary">Loading balances...</Typography>
      ) : paginatedBalances.length === 0 ? (
        <Typography color="text.secondary">
          {baseBalances.length === 0 ? 'No players yet' : `No players match the "${filter}" filter`}
        </Typography>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 650 }}>
            <thead>
              <tr style={{ backgroundColor: '#f5f5f5' }}>
                <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 600, fontSize: '0.75rem' }}>Player (Squares)</th>
                <th style={{ textAlign: 'right', padding: '8px 12px', fontWeight: 600, fontSize: '0.75rem', color: '#1976d2', width: 80 }}>Cost</th>
                <th style={{ textAlign: 'right', padding: '8px 12px', fontWeight: 600, fontSize: '0.75rem', color: '#2e7d32', width: 80 }}>Paid</th>
                <th style={{ textAlign: 'right', padding: '8px 12px', fontWeight: 600, fontSize: '0.75rem', color: '#0288d1', width: 80 }}>Winnings</th>
                <th style={{ textAlign: 'right', padding: '8px 12px', fontWeight: 600, fontSize: '0.75rem', width: 90 }}>Balance</th>
                {isManager && <th style={{ textAlign: 'center', padding: '8px 12px', fontWeight: 600, fontSize: '0.75rem', width: 140 }}>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {paginatedBalances.map((balance) => {
                const amountDue = balance.totalOwed - balance.totalPaid;
                const isPaid = amountDue <= 0;
                const hasWinnings = balance.totalEarned > 0;
                const balanceKey = balance.isOfflinePlayer ? `offline-${balance.offlinePlayerId}` : `user-${balance.userId}`;
                const isExpanded = expandedRows.has(balanceKey);
                const winningHits = hasWinnings ? getWinningHitsForUser(
                  balance.isOfflinePlayer ? undefined : balance.userId,
                  balance.isOfflinePlayer ? balance.offlinePlayerId : undefined
                ) : [];
                const badNumbersEntries = hasWinnings ? getBadNumbersEntriesForUser(
                  balance.isOfflinePlayer ? undefined : balance.userId,
                  balance.isOfflinePlayer ? balance.offlinePlayerId : undefined
                ) : [];
                const neighborPayoutEntries = hasWinnings ? getNeighborPayoutEntriesForUser(
                  balance.isOfflinePlayer ? undefined : balance.userId,
                  balance.isOfflinePlayer ? balance.offlinePlayerId : undefined
                ) : [];
                const claimedCells = Math.round(balance.claimedSquares / squaresPerCell);
                const approvedCells = balance.approvedSquares;
                const pendingMergeForPlayer = balance.isOfflinePlayer && balance.offlinePlayerId
                  ? pendingMergeRequests.find(r => r.offlinePlayerId === balance.offlinePlayerId)
                  : null;
                const pendingMergeAsTarget = !balance.isOfflinePlayer && balance.userId
                  ? pendingMergeRequests.find(r => r.targetUserId === balance.userId)
                  : null;

                return (
                  <React.Fragment key={balanceKey}>
                    <tr
                      style={{
                        backgroundColor: isPaid ? '#e8f5e9' : hasWinnings ? '#e3f2fd' : '#fff',
                        borderBottom: isExpanded ? 'none' : '1px solid #e0e0e0',
                        cursor: hasWinnings ? 'pointer' : 'default',
                      }}
                      onClick={() => hasWinnings && toggleRowExpansion(balanceKey)}
                    >
                      <td style={{ padding: '10px 12px', maxWidth: 160 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                          {hasWinnings && (
                            <Typography variant="body2" sx={{ color: 'text.secondary', mr: 0.5, flexShrink: 0 }}>
                              {isExpanded ? '▼' : '▶'}
                            </Typography>
                          )}
                          {balance.isOfflinePlayer ? (
                            <Tooltip title="Offline Player"><OfflinePlayerIcon sx={{ fontSize: 16, color: 'text.secondary', flexShrink: 0 }} /></Tooltip>
                          ) : (
                            <Tooltip title="Online Player"><PersonIcon sx={{ fontSize: 16, color: 'text.secondary', flexShrink: 0 }} /></Tooltip>
                          )}
                          <Typography variant="body2" fontWeight="bold" noWrap sx={{ overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0, flex: 1 }}>
                            {getDisplayName(balance)}
                          </Typography>
                          <Chip
                            label={`${claimedCells}/${approvedCells}`}
                            size="small"
                            sx={{
                              ml: 1, height: 20, fontSize: '0.7rem', flexShrink: 0,
                              bgcolor: claimedCells > approvedCells ? 'error.light' : claimedCells === approvedCells && approvedCells > 0 ? 'success.light' : 'grey.200',
                              color: claimedCells > approvedCells || (claimedCells === approvedCells && approvedCells > 0) ? 'white' : 'text.primary',
                            }}
                          />
                          {pendingMergeForPlayer && (
                            <Tooltip title={`Merge pending: waiting for ${pendingMergeForPlayer.targetUser?.displayName || 'user'} to approve`}>
                              <Chip icon={<MergeIcon sx={{ fontSize: 14 }} />} label="Merge Pending" size="small" color="warning" variant="outlined" sx={{ ml: 1, height: 20, fontSize: '0.65rem', flexShrink: 0 }} />
                            </Tooltip>
                          )}
                          {pendingMergeAsTarget && (
                            <Tooltip title={`${pendingMergeAsTarget.offlinePlayer?.displayName || 'Player'}'s squares will merge into your account if approved`}>
                              <Chip icon={<MergeIcon sx={{ fontSize: 14 }} />} label="Merge Request" size="small" color="info" variant="outlined" sx={{ ml: 1, height: 20, fontSize: '0.65rem', flexShrink: 0 }} />
                            </Tooltip>
                          )}
                        </Box>
                      </td>
                      <td style={{ textAlign: 'right', padding: '10px 12px' }}>
                        <Typography variant="body2" color="text.secondary">${balance.totalOwed.toFixed(2)}</Typography>
                      </td>
                      <td style={{ textAlign: 'right', padding: '10px 12px' }}>
                        <Typography variant="body2" color="success.main" fontWeight={balance.totalPaid > 0 ? 'bold' : 'normal'}>${balance.totalPaid.toFixed(2)}</Typography>
                      </td>
                      <td style={{ textAlign: 'right', padding: '10px 12px' }}>
                        <Typography variant="body2" color={hasWinnings ? 'info.main' : 'text.secondary'} fontWeight={hasWinnings ? 'bold' : 'normal'}>${balance.totalEarned.toFixed(2)}</Typography>
                      </td>
                      <td style={{ textAlign: 'right', padding: '10px 12px' }}>
                        <Typography variant="body2" fontWeight="bold" color={balance.netBalance >= 0 ? 'success.main' : 'error.main'}>
                          {balance.netBalance >= 0 ? '+' : '-'}${Math.abs(balance.netBalance).toFixed(2)}
                        </Typography>
                      </td>
                      {isManager && (
                        <td style={{ textAlign: 'center', padding: '10px 12px', width: 140 }} onClick={(e) => e.stopPropagation()}>
                          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                            {isCompleted && hasWinnings && !balance.winningsFullyPaid && (
                              <Button size="small" variant="text" onClick={() => handleOpenPaymentDialog(balance)} sx={{ fontSize: '0.7rem', py: 0, minWidth: 'auto', px: 1 }}>+ Pay</Button>
                            )}
                            {hasWinnings && canMarkWinningsPaid && (
                              <Tooltip title={balance.winningsFullyPaid ? 'Mark winnings as unpaid' : 'Mark winnings as paid'}>
                                <Button size="small" variant="text" onClick={() => handleWinningsPaidChange(balance, balance.winningsFullyPaid)} sx={{ fontSize: '0.7rem', py: 0, minWidth: 'auto', px: 1, color: balance.winningsFullyPaid ? 'success.main' : 'warning.main' }}>
                                  {balance.winningsFullyPaid ? '✓ Won' : '○ Won'}
                                </Button>
                              </Tooltip>
                            )}
                            {balance.isOfflinePlayer && balance.offlinePlayer && (
                              <>
                                {!isBoardLocked && (
                                  <Tooltip title="Edit offline player">
                                    <IconButton size="small" onClick={() => handleOpenDialog(balance.offlinePlayer!)} sx={{ p: 0.5 }}><EditIcon sx={{ fontSize: 16 }} /></IconButton>
                                  </Tooltip>
                                )}
                                {(!isBoardLocked || balance.claimedSquares === 0) && (
                                  <Tooltip title="Remove from board">
                                    <IconButton size="small" onClick={() => handleRemoveFromBoard(balance.offlinePlayerId!)} sx={{ p: 0.5, color: 'error.main' }}><DeleteIcon sx={{ fontSize: 16 }} /></IconButton>
                                  </Tooltip>
                                )}
                              </>
                            )}
                            {!balance.isOfflinePlayer && balance.userId && (() => {
                              const player = (players || []).find(p => p.userId === balance.userId);
                              const playerRole = player?.role;
                              const name = getDisplayName(balance);
                              if (playerRole === 'player' || (!playerRole && balance.userId !== currentUserId)) {
                                return (
                                  <>
                                    {!isBoardLocked && (
                                      <Tooltip title="Assign squares"><IconButton size="small" onClick={() => handleOpenAssignSquares(balance.userId, name)} sx={{ p: 0.5, color: 'primary.main' }}><GridIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
                                    )}
                                    {!isBoardLocked && (
                                      <Tooltip title="Promote to manager"><IconButton size="small" onClick={() => handleOpenPromoteConfirm(balance.userId, name)} sx={{ p: 0.5 }}><PersonIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
                                    )}
                                    {onRemoveParticipant && (!isBoardLocked || balance.claimedSquares === 0) && (
                                      <Tooltip title="Remove from board"><IconButton size="small" onClick={() => handleOpenRemoveParticipantConfirm(balance.userId, name)} sx={{ p: 0.5, color: 'error.main' }}><DeleteIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
                                    )}
                                  </>
                                );
                              }
                              if (isOwner && playerRole === 'manager') {
                                return (
                                  <Tooltip title="Remove manager"><IconButton size="small" onClick={() => handleOpenRemoveConfirm(balance.userId, name)} sx={{ p: 0.5, color: 'error.main' }}><PersonIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
                                );
                              }
                              return null;
                            })()}
                          </Box>
                        </td>
                      )}
                    </tr>
                    {/* Expanded winnings breakdown row */}
                    {isExpanded && hasWinnings && (
                      <tr>
                        <td colSpan={isManager ? 7 : 6} style={{ padding: 0, backgroundColor: '#f8f9fa' }}>
                          <Box sx={{ px: 3, py: 1.5, borderBottom: '1px solid #e0e0e0' }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold', display: 'block', mb: 1 }}>
                              Winnings Breakdown
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                              {badNumbersEntries.map((entry) => (
                                <Box key={entry.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5, px: 1, backgroundColor: '#fff', borderRadius: 0.5, border: '1px solid #e0e0e0' }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Chip label="Bad #" size="small" sx={{ fontSize: '0.65rem', height: 20, bgcolor: '#ffebee', color: '#c62828' }} />
                                    <Typography variant="caption">{entry.description}</Typography>
                                  </Box>
                                  <Typography variant="caption" fontWeight="bold" color="success.main">${entry.amount.toFixed(2)}</Typography>
                                </Box>
                              ))}
                              {winningHits.map((hit) => (
                                <Box key={hit.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5, px: 1, backgroundColor: '#fff', borderRadius: 0.5, border: '1px solid #e0e0e0' }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Chip label={getHitTypeLabel(hit)} size="small" sx={{ fontSize: '0.65rem', height: 20, bgcolor: (hit.isPlusN && hit.isWrongWay) ? '#fff3e0' : hit.isPlusN ? '#e3f2fd' : hit.isWrongWay ? '#fff3e0' : hit.categoryId ? '#f3e5f5' : '#e8f5e9', color: (hit.isPlusN && hit.isWrongWay) ? '#e65100' : hit.isPlusN ? '#1565c0' : hit.isWrongWay ? '#e65100' : hit.categoryId ? '#7b1fa2' : '#2e7d32' }} />
                                    <Typography variant="caption">
                                      {hit.categoryId ? hit.description : getQuarterLabel(hit.quarter)}
                                      {' • '}
                                      {board.homeTeam} {hit.homeScore} - {board.awayTeam} {hit.awayScore}
                                    </Typography>
                                  </Box>
                                  <Typography variant="caption" fontWeight="bold" color="success.main">${hit.payoutAmount.toFixed(2)}</Typography>
                                </Box>
                              ))}
                              {neighborPayoutEntries.map((entry) => (
                                <Box key={entry.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5, px: 1, backgroundColor: '#fff', borderRadius: 0.5, border: '1px solid #e0e0e0' }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Chip label="Neighbor" size="small" sx={{ fontSize: '0.65rem', height: 20, bgcolor: '#e1f5fe', color: '#0277bd' }} />
                                    <Typography variant="caption">{entry.description}</Typography>
                                  </Box>
                                  <Typography variant="caption" fontWeight="bold" color="success.main">${entry.amount.toFixed(2)}</Typography>
                                </Box>
                              ))}
                            </Box>
                          </Box>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </Box>
      )}

      {/* Payment Dialog */}
      <Dialog open={paymentDialogOpen} onClose={() => setPaymentDialogOpen(false)}>
        <DialogTitle>Record Payment</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>Recording payment from <strong>{selectedUser?.name}</strong></Typography>
          <TextField fullWidth type="number" label="Amount" value={paymentInput.value} onChange={paymentInput.onChange} onBlur={paymentInput.onBlur} sx={{ mt: 2 }} inputProps={{ min: 0, step: 0.01 }} />
          <TextField fullWidth label="Notes (optional)" placeholder="e.g., Venmo txn #12345, Cash received" value={paymentNotes} onChange={(e) => setPaymentNotes(e.target.value)} sx={{ mt: 2 }} multiline rows={2} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaymentDialogOpen(false)} disabled={saving}>Cancel</Button>
          <Button onClick={handleRecordPayment} variant="contained" disabled={saving || paymentAmount <= 0}>
            {saving ? 'Saving...' : 'Record Payment'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create/Edit Offline Player Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingPlayer ? 'Edit Offline Player' : 'Create Offline Player'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField fullWidth label="Display Name" value={displayName} onChange={(e) => setDisplayName(e.target.value)} required error={!!playerError && !displayName.trim()} helperText={!displayName.trim() && playerError ? 'Display name is required' : ''} />
            <TextField fullWidth label="Handle (4 characters)" value={handle} onChange={(e) => setHandle(e.target.value.slice(0, 4).toUpperCase())} placeholder="e.g., JOHN" helperText="4-character code shown on squares" inputProps={{ maxLength: 4 }} />
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>Avatar Icon (optional)</Typography>
              <IconPicker value={iconName} onChange={setIconName} />
            </Box>
            <TextField fullWidth type="number" label="Approved Squares" value={approvedSquaresInput.value} onChange={approvedSquaresInput.onChange} onBlur={approvedSquaresInput.onBlur} inputProps={{ min: 0, max: 100 }} helperText="Max squares this player can claim (0 = unlimited)" />
            {!editingPlayer && (
              <TextField fullWidth type="number" label="Starting Balance" value={startingBalanceInput.value} onChange={startingBalanceInput.onChange} onBlur={startingBalanceInput.onBlur} inputProps={{ min: 0, step: 0.01 }} helperText="Pre-paid amount (e.g., cash received upfront)" InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} />
            )}
            {playerError && <Alert severity="error">{playerError}</Alert>}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={saving}>Cancel</Button>
          <Button onClick={handleSavePlayer} variant="contained" disabled={saving || !displayName.trim()}>
            {saving ? 'Saving...' : editingPlayer ? 'Save' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Pagination */}
      {sortedBalances.length > 10 && (
        <TablePagination component="div" count={sortedBalances.length} {...paginationProps} />
      )}

      {/* Promote to Manager Confirmation Dialog */}
      <ConfirmDialog open={promoteConfirmOpen} title="Make Manager" message={`Are you sure you want to make ${pendingManagerAction?.displayName || 'this player'} a manager? Managers can edit the board, record scores, and manage squares.`} confirmLabel="Make Manager" confirmColor="primary" onConfirm={handleConfirmPromote} onCancel={() => { setPromoteConfirmOpen(false); setPendingManagerAction(null); }} />

      {/* Remove Manager Confirmation Dialog */}
      <ConfirmDialog open={removeConfirmOpen} title="Remove Manager" icon="warning" message={`Are you sure you want to remove ${pendingManagerAction?.displayName || 'this player'} as a manager? They will no longer be able to edit the board or record scores.`} confirmLabel="Remove Manager" confirmColor="error" onConfirm={handleConfirmRemove} onCancel={() => { setRemoveConfirmOpen(false); setPendingManagerAction(null); }} />

      {/* Remove Participant Confirmation Dialog */}
      <ConfirmDialog open={removeParticipantConfirmOpen} title="Remove Player" icon="warning" message={`Are you sure you want to remove ${pendingParticipantRemoval?.displayName || 'this player'} from the board? Their squares will be cleared and any square requests will be deleted.`} confirmLabel="Remove Player" confirmColor="error" onConfirm={handleConfirmRemoveParticipant} onCancel={() => { setRemoveParticipantConfirmOpen(false); setPendingParticipantRemoval(null); }} />

      {/* Assign Squares to Online Player Dialog */}
      <Dialog open={assignSquaresDialogOpen} onClose={handleCloseAssignSquares} maxWidth="xs" fullWidth>
        <DialogTitle>Assign Squares</DialogTitle>
        <DialogContent>
          {assignSquaresError && <Alert severity="error" sx={{ mb: 2 }}>{assignSquaresError}</Alert>}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Assign squares to <strong>{assignSquaresTarget?.displayName}</strong>. This creates an auto-approved request, allowing them to claim the specified number of squares.
          </Typography>
          <TextField autoFocus fullWidth type="number" label="Number of Squares" value={assignSquaresCount} onChange={(e) => { const val = parseInt(e.target.value, 10); setAssignSquaresCount(isNaN(val) ? 1 : Math.min(100, Math.max(1, val))); }} disabled={assigningSquares} inputProps={{ min: 1, max: 100 }} helperText="How many squares to assign to this player (1-100)" sx={{ mt: 1 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAssignSquares} disabled={assigningSquares}>Cancel</Button>
          <Button onClick={handleConfirmAssignSquares} variant="contained" disabled={assigningSquares || assignSquaresCount <= 0} startIcon={assigningSquares ? <CircularProgress size={16} /> : <GridIcon />}>
            {assigningSquares ? 'Assigning...' : 'Assign Squares'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Accounting Summary Modal */}
      <Dialog open={summaryModalOpen} onClose={() => setSummaryModalOpen(false)} maxWidth="sm" fullWidth scroll="paper">
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><ReceiptIcon />Accounting Summary</Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Copy this summary to share with players (e.g., post on social media). Positive amounts = player is owed, negative = player owes.
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50', fontFamily: 'monospace', fontSize: '0.9rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word', minHeight: 100, maxHeight: 400, overflow: 'auto' }}>
            {generateAccountingSummary(baseBalances) || 'No players yet'}
          </Paper>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSummaryModalOpen(false)}>Close</Button>
          <Button variant="contained" startIcon={copiedSummary ? <ApproveIcon /> : <CopyIcon />} onClick={handleCopySummary} color={copiedSummary ? 'success' : 'primary'}>
            {copiedSummary ? 'Copied!' : 'Copy to Clipboard'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Merge Offline Player Dialog */}
      <MergeOfflinePlayerDialog
        open={mergeDialogOpen}
        offlinePlayers={offlinePlayers as BoardOfflinePlayer[]}
        participants={participants}
        boardId={board.id}
        currentUserId={currentUserId}
        onClose={() => setMergeDialogOpen(false)}
        onRequestCreated={handleRequestCreated}
        onGlobalRequestCreated={handleGlobalRequestCreated}
        initialOfflinePlayerId={mergeSuggestionOfflinePlayerId}
        initialTargetUserId={mergeSuggestionTargetUserId}
      />

      {/* Success message for merge */}
      <Snackbar open={!!mergeSuccess} autoHideDuration={5000} onClose={() => setMergeSuccess(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="success" onClose={() => setMergeSuccess(null)}>{mergeSuccess}</Alert>
      </Snackbar>
    </Box>
  );
};

export default LedgerPanel;
